import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import useAnimationOnView from '@/data/hook/useMotionViewAnimation';
import Lesson from '@/models/Lesson';
import LessonProgress from '@/models/LessonProgress';
import ScheduledLesson, {
  ScheduledLessonTypeEnum,
} from '@/models/ScheduledLesson';
import { buildMotionLIProps } from '@/utils/animations/lessonCardAnimation';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import LessonBarLink from './LessonBarLink';
import LessonBarIcons from './LessonBarIcons';
import LessonBarDate from './LessonBarDate';
import LessonProgressBar from './LessonProgressBar';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import BackButton from '@/components/ui/custom/BackButton';
import { pathChecker } from '@/utils/pathChecker';
import LessonBarStatus from './LessonBarStatus';
import { ResourceProgress } from '@/models/ResourceProgress';
import Resource from '@/models/_deprecated/Resource';

export type LessonBarColor = 'primary' | 'secondary' | 'accent';

export type LessonBarProps = {
  lesson: Lesson;
  scheduledLesson?: ScheduledLesson;
  slugCourseName?: string;
  lessonProgress?: LessonProgress;
  index?: number;
  blocked?: boolean;
  color?: LessonBarColor;
  backUrl?: string;
  hasBackbutton?: boolean;
  hideProgress?: boolean;
  klassId?: number;
  resourceProgress?: ResourceProgress[];
  resources?: Resource[];
  isStaff?: boolean;
  blockedMessage?: string;
};

export default function LessonBar({
  lesson,
  lessonProgress,
  scheduledLesson,
  blocked,
  index = 0,
  color,
  slugCourseName,
  backUrl,
  hasBackbutton,
  klassId,
  resourceProgress = [],
  resources = [],
  isStaff,
  blockedMessage,
}: LessonBarProps) {
  const { t } = useTranslation('translation');

  const freeLesson = scheduledLesson?.type === ScheduledLessonTypeEnum.FREE;

  const { variants, custom } = buildMotionLIProps(
    index,
    'right',
    index < 5 ? 0.1 : 0,
  );

  const motionLiProps = useAnimationOnView({
    variants,
    custom,
  });

  const setBgColor = (color?: LessonBarColor) => {
    const bgColors: { [key in LessonBarColor]: string } = {
      primary: 'bg-primary-content',
      secondary: 'bg-secondary-content',
      accent: 'bg-accent-content',
    };

    return bgColors[color ?? 'primary'];
  };

  const className = `rounded-lg shadow-xs flex flex-col w-full ${
    blocked ? 'opacity-60 disabled' : 'cursor-pointer'
  } ${setBgColor(color) ?? 'bg-secondary-content'} items-center relative`;

  const hideCondition = pathChecker();

  return (
    <motion.li {...motionLiProps} data-testid="lessonBar" className={className}>
      <ConditionalWrapper
        condition={blocked ?? false}
        wrapper={
          <Tooltip
            options={{ placement: 'auto' }}
            text={blockedMessage ?? t('klassProgressPage.lessonBlocked')}
            classNameContainer="w-full"
          />
        }
      >
        <div className="relative flex w-full justify-between pr-4">
          <LessonBarLink
            slugCourseName={slugCourseName}
            lesson={lesson}
            lessonProgress={lessonProgress}
            scheduledLesson={scheduledLesson}
            color={color}
            blocked={blocked}
            hideProgress={freeLesson}
            klassId={klassId}
            resourceProgress={resourceProgress}
            resources={resources}
            isStaff={isStaff}
          />
          <div className="flex gap-4 items-center shrink-0">
            <LessonBarStatus status={lesson.status} />
            <ConditionalRenderer condition={!hideCondition}>
              <LessonBarIcons
                lesson={lesson}
                lessonProgress={lessonProgress}
                scheduledLesson={scheduledLesson}
                slugCourseName={slugCourseName}
                blocked={blocked}
                hideProgress={freeLesson}
                klassId={klassId}
                resourceProgress={resourceProgress}
                resources={resources}
                isStaff={isStaff}
              />

              <LessonBarDate
                scheduledLesson={scheduledLesson}
                presence={lessonProgress?.scheduledLessonReport?.presence}
              />
            </ConditionalRenderer>
            <ConditionalRenderer condition={hasBackbutton}>
              <span className="hidden md:flex items-center">
                <BackButton url={backUrl} />
              </span>
            </ConditionalRenderer>
          </div>
        </div>
        <LessonProgressBar
          progress={lessonProgress?.progress}
          color={color}
          hideProgress={freeLesson}
        />
      </ConditionalWrapper>
    </motion.li>
  );
}
